export default function AICapabilities() {
  const capabilities = [
    {
      icon: 'ri-brain-line',
      title: 'Intelligent Automation',
      description: 'Our AI engines handle repetitive, high-volume tasks with precision — from ticket routing and data extraction to quality assurance and compliance checks — freeing your people for higher-value work.'
    },
    {
      icon: 'ri-message-3-line',
      title: 'Conversational AI',
      description: 'Deploy AI-powered chat and voice agents that understand context, resolve issues instantly, and escalate to human agents only when it truly matters — delivering 24/7 support without the overhead.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Predictive Analytics',
      description: 'Real-time dashboards and AI-driven forecasting give you full visibility into performance, customer sentiment, and operational health — so you can make smarter decisions faster.'
    },
    {
      icon: 'ri-user-settings-line',
      title: 'AI-Augmented Teams',
      description: 'Every agent on our team is equipped with AI co-pilot tools — live suggestions, knowledge retrieval, and sentiment analysis — so they perform at a level no traditional BPO can match.'
    }
  ];

  const differentiators = [
    { label: 'Safe', icon: 'ri-lock-line', desc: 'SOC 2, HIPAA, PCI-DSS & GDPR compliant infrastructure with AI-powered threat monitoring.' },
    { label: 'Flexible', icon: 'ri-swap-line', desc: 'Tailored delivery models that adapt to your business — onshore, offshore, or hybrid.' },
    { label: 'Innovative', icon: 'ri-lightbulb-flash-line', desc: 'We continuously invest in AI R&D so your operations stay ahead of the curve, not behind it.' },
  ];

  return (
    <section className="py-24 bg-[#0a0f1e] relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}
      ></div>
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full filter blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-4">
            <i className="ri-sparkling-2-line text-blue-400 text-sm"></i>
            <span className="text-sm font-medium text-blue-300">AI at Our Core</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            What Sets Us Apart
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            We're not a traditional BPO that bolted AI on as an afterthought. AI is the foundation of everything we build — every team, every process, every outcome.
          </p>
        </div>

        {/* Capability cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {capabilities.map((cap, i) => (
            <div key={i} className="group bg-white/5 border border-white/10 rounded-xl p-8 hover:bg-white/10 hover:border-blue-500/40 transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-blue-500/20 rounded-xl border border-blue-500/30 mb-6 group-hover:bg-blue-600/30 transition-colors">
                <i className={`${cap.icon} text-2xl text-blue-400`}></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{cap.title}</h3>
              <p className="text-gray-400 leading-relaxed">{cap.description}</p>
            </div>
          ))}
        </div>

        {/* Differentiators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {differentiators.map((d, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 flex gap-4 items-start hover:border-blue-500/30 transition-all">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-500/20 rounded-lg shrink-0">
                <i className={`${d.icon} text-lg text-blue-400`}></i>
              </div>
              <div>
                <div className="text-white font-bold mb-1">{d.label}</div>
                <p className="text-gray-400 text-sm leading-relaxed">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* AI visual showcase */}
        <div className="relative rounded-2xl overflow-hidden border border-white/10">
          <img
            src="https://public.readdy.ai/ai/img_res/2386bffb0c6f276a4a8d1b9d2623f708.jpg"
            alt="AI Operations Center"
            className="w-full h-64 object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1e] via-transparent to-[#0a0f1e]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">
                AI-Powered <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Operations</span>
              </div>
              <p className="text-gray-300 text-lg">Running smarter for 500+ businesses worldwide</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
